import { useState, useEffect } from "react";
import { getPGManagementDashboard } from "../../../services/propertyOwner/propertyOwnerServices";

const fmt = (n) => (n != null ? `₹${Number(n).toLocaleString("en-IN")}` : "—");

const badgeForPct = (pct) => {
  if (pct === 0) return "badge-gray";
  if (pct < 50) return "badge-warning";
  if (pct < 80) return "badge-info";
  return "badge-active";
};

function SkeletonCard() {
  return (
    <div className="stat-card">
      {[60, 40, 50].map((w, i) => (
        <div
          key={i}
          style={{
            height: i === 1 ? 28 : 12,
            width: `${w}%`,
            background: "var(--border)",
            borderRadius: 4,
            marginBottom: 10,
            opacity: 0.5,
          }}
        />
      ))}
    </div>
  );
}

function PropertyCard({ property }) {
  const {
    name,
    city,
    stateName,
    totalBeds,
    occupiedBeds,
    vacantBeds,
    occupancyPercent,
  } = property;
  const pct = occupancyPercent ?? 0;

  return (
    <div className="stat-card" style={{ cursor: "default" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <div style={{ fontSize: 18 }}>🏠</div>
        <span className={`badge ${badgeForPct(pct)}`}>{pct}% Full</span>
      </div>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>
        {name}
      </div>
      <div
        style={{
          fontSize: 11,
          color: "var(--text-secondary)",
          marginBottom: 8,
        }}
      >
        {[city, stateName].filter(Boolean).join(", ")}
      </div>
      <div style={{ display: "flex", gap: 12, fontSize: 12 }}>
        <span>🛏 {totalBeds} beds</span>
        <span style={{ color: "var(--green)" }}>✅ {occupiedBeds} occ.</span>
        <span style={{ color: "var(--amber)" }}>🔓 {vacantBeds} vacant</span>
      </div>
      <div className="progress-bar" style={{ marginTop: 10 }}>
        <div
          className="progress-fill"
          style={{
            width: `${pct}%`,
            background: pct === 0 ? "var(--amber)" : "var(--blue)",
          }}
        />
      </div>
    </div>
  );
}

function TenantRow({ tenant }) {
  const {
    fullName,
    mobile,
    pgPropertyName,
    roomName,
    bedNumber,
    sharingTypeName,
    monthlyBill,
    isActive,
  } = tenant;

  const statusBadge = isActive ? "badge-active" : "badge-gray";
  const statusLabel = isActive ? "Active" : "Inactive";

  return (
    <tr>
      <td>
        <div style={{ fontWeight: 600 }}>{fullName ?? "—"}</div>
        {mobile && (
          <div style={{ fontSize: 10, color: "var(--text-secondary)" }}>
            📱 {mobile}
          </div>
        )}
      </td>
      <td style={{ fontSize: 11 }}>{pgPropertyName ?? "—"}</td>
      <td style={{ fontSize: 11 }}>
        {roomName ?? "—"}
        {bedNumber != null ? ` · Bed ${bedNumber}` : ""}
      </td>
      <td style={{ fontSize: 11 }}>{sharingTypeName ?? "—"}</td>
      <td style={{ fontWeight: 600 }}>
        {monthlyBill != null
          ? `₹${Number(monthlyBill).toLocaleString("en-IN")}/mo`
          : "—"}
      </td>
      <td>
        <span className={`badge ${statusBadge}`}>{statusLabel}</span>
      </td>
    </tr>
  );
}
export default function PgDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchDashboard = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getPGManagementDashboard();
        if (!cancelled) {
          // API shape: { statusCode, message, data: { ... } }
          setData(response?.data?.data ?? response?.data ?? response);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ??
              err?.message ??
              "Something went wrong.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDashboard();
    return () => {
      cancelled = true;
    };
  }, []);

  const totalProperties = data?.totalProperties ?? 0;
  const totalRooms = data?.totalRooms ?? 0;
  const totalBeds = data?.totalBeds ?? 0;
  const occupiedBeds = data?.occupiedBeds ?? 0;
  const vacantBeds = data?.vacantBeds ?? 0;
  const occupancyPercent = data?.occupancyPercent ?? 0;
  const monthlyRevenue = data?.monthlyRevenue ?? 0;
  const properties = data?.properties ?? [];
  const recentTenants = data?.recentTenants ?? [];

  return (
    <div className="main">
      <div className="topbar">
        <div>
          <div className="page-title" id="page-title">
            PG Dashboard
          </div>
          <div className="page-sub" id="page-sub">
            Paying Guest management overview
          </div>
        </div>
        <div className="topbar-right" id="topbar-right" />
      </div>

      <div className="content" id="content-area">
        {/* Error banner */}
        {error && (
          <div
            style={{
              background: "var(--red-light, #fff1f0)",
              border: "1px solid var(--red, #ff4d4f)",
              borderRadius: 8,
              padding: "10px 16px",
              marginBottom: 16,
              color: "var(--red, #cf1322)",
              fontSize: 13,
            }}
          >
            ⚠️ {error}
          </div>
        )}

        {/* Stat cards */}
        <div className="stat-grid-5 mb16">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)
          ) : (
            <>
              <div className="stat-card">
                <div className="stat-icon">🏠</div>
                <div className="stat-label">PG Properties</div>
                <div className="stat-value">{totalProperties}</div>
                <div className="stat-delta delta-up">Active</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🚪</div>
                <div className="stat-label">Total Rooms</div>
                <div className="stat-value">{totalRooms}</div>
                <div className="stat-delta delta-neu">Across all PGs</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🛏</div>
                <div className="stat-label">Total Beds</div>
                <div className="stat-value">{totalBeds}</div>
                <div className="stat-delta delta-neu">Capacity</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">✅</div>
                <div className="stat-label">Occupied Beds</div>
                <div className="stat-value" style={{ color: "var(--green)" }}>
                  {occupiedBeds}
                </div>
                <div className="stat-delta delta-up">
                  {occupancyPercent}% occupancy
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">💰</div>
                <div className="stat-label">Monthly Revenue</div>
                <div
                  className="stat-value"
                  style={{ color: "var(--blue)", fontSize: 18 }}
                >
                  {fmt(monthlyRevenue)}
                </div>
                <div className="stat-delta delta-up">from active tenants</div>
              </div>
            </>
          )}
        </div>

        {/* Property cards */}
        {!loading && properties.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 14,
              marginBottom: 20,
            }}
          >
            {properties.map((property) => (
              <PropertyCard key={property.pgPropertyId} property={property} />
            ))}
          </div>
        )}

        {/* Recent tenants */}
        <div className="card">
          <div className="card-title">Recent PG Tenants</div>

          {loading ? (
            <div
              style={{
                padding: "24px 0",
                textAlign: "center",
                color: "var(--text-secondary)",
                fontSize: 13,
              }}
            >
              Loading tenants…
            </div>
          ) : recentTenants.length === 0 ? (
            <div
              style={{
                padding: "24px 0",
                textAlign: "center",
                color: "var(--text-secondary)",
                fontSize: 13,
              }}
            >
              No tenants found.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>PG Property</th>
                    <th>Room / Bed</th>
                    <th>Sharing</th>
                    <th>Monthly Bill</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTenants.map((tenant, i) => (
                    <TenantRow key={tenant.id ?? i} tenant={tenant} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
